<template>
  <div>
    <v-container v-if="workpackage" fluid pr-0 pl-0 pb-0 pt-6>
      <v-row no-gutters class="header text-row pl-4">
        <v-col class="pb-0 pt-2">
          {{ $t('workPackages.owner') }}: <strong>{{ workpackage.owner }}</strong>
        </v-col>
      </v-row>
      <v-row no-gutters class="header text-row pl-4">
        <v-col class="pb-0 pt-2">
          <span>
            {{ $t('workPackages.createdOn') }}:
            <br />
            <strong>{{ formatLocalisedDate(workpackage.creationDate) }}</strong>
          </span>
        </v-col>
      </v-row>
      <v-row v-if="workpackage.lastUpdated" no-gutters class="header text-row pl-4">
        <v-col class="pb-0 pt-2">
          <span>
            {{ $t('workPackages.lastUpdated') }}:
            <feature-toggle v-if="!isMasterWorkpackage" toggle="refreshWorkpackageFromDatabricks">
              <progress-spinner
                v-if="refreshing"
                :job-id="workpackage.description"
                job-type="run_refresh_workpackage"
              />
              <v-btn
                v-else
                x-small
                depressed
                class="refresh-btn"
                :disabled="disabled"
                @click="isRefreshConfirmOpen = true"
              >
                <v-icon v-once>mdi-autorenew</v-icon>
              </v-btn>
            </feature-toggle>
            <br />
            <strong>{{ lastUpdatedText }}</strong>
          </span>
        </v-col>
      </v-row>
      <v-row
        v-if="workpackage.competitorLastUpdated && isMasterWorkpackage"
        no-gutters
        class="header text-row pl-4"
      >
        <v-col class="pb-0 pt-2">
          <span>
            {{ $t('workPackages.competitorLastUpdated') }}:
            <br />
            <strong>{{ getFormattedDateTime(workpackage.competitorLastUpdated) }}</strong>
          </span>
        </v-col>
      </v-row>
      <v-row
        v-if="workpackage.cumulativeProductCount && isMasterWorkpackage"
        no-gutters
        class="header text-row pl-4"
      >
        <v-col class="pb-0 pt-2">
          <span>
            {{ $t('workPackages.documents') }}
            <br />
            <strong>{{
              $t('workPackages.documentsCount', {
                previousProductCount: formatNumber({
                  number: workpackage.previousProductCount,
                  format: numberFormats.integer,
                }),
                cumulativeProductCount: formatNumber({
                  number: workpackage.cumulativeProductCount,
                  format: numberFormats.integer,
                }),
              })
            }}</strong>
          </span>
        </v-col>
      </v-row>
      <v-row no-gutters class="text-row pl-4">
        <v-col v-if="workpackage.releaseDate" class="pb-0 pt-2">
          {{ $t('workPackages.releaseDate') }}: <strong>{{ workpackage.releaseDate }}</strong>
        </v-col>
      </v-row>
      <feature-toggle toggle="enableAOExtracts">
        <template v-if="workpackage.aoExtracts && workpackage.aoExtracts.length > 0">
          <v-row no-gutters class="text-row pl-4">
            <v-col class="pb-0 pt-2 ao-extracts">
              <span>{{ $t('workPackages.aoExtracts') }}:</span>
            </v-col>
          </v-row>

          <v-row
            v-for="extract in workpackage.aoExtracts"
            :key="extract.auditCorrelationId"
            no-gutters
            class="text-row pl-4"
          >
            <v-col class="pb-0 pt-2 ao-extracts">
              <strong>{{ getAOExtractsByKey[extract.auditCorrelationId].displayName }}</strong>
            </v-col>
          </v-row>

          <feature-toggle toggle="usePeriodIdsInAOExtracts">
            <v-row no-gutters class="text-row pl-4">
              <v-col class="pb-0 pt-2 ao-extracts">
                <span>{{ $t('workPackages.associatedPeriodIds') }}:</span>
              </v-col>
            </v-row>

            <v-row
              v-for="periodId in getWorkpackageAssociatedPeriodIds"
              :key="periodId"
              no-gutters
              class="text-row pl-4"
            >
              <v-col class="pb-0 pt-2 ao-extracts">
                <strong>{{ periodId }}</strong>
              </v-col>
            </v-row>
          </feature-toggle>

          <v-row v-if="workpackage.missingProductsInAOExtracts" no-gutters class="text-row pl-4">
            <v-col class="pb-0 pt-2">
              <v-icon color="orange darken-2">warning</v-icon>
              {{
                $t('workPackages.missingProductsInAOExtracts', {
                  count: workpackage.missingProductsInAOExtracts.length,
                })
              }}

              <v-btn class="ml-2" text outlined small @click="isMissingProductsDialogOpen = true">
                {{ $t('actions.seeAll') }}
              </v-btn>

              <info-dialog
                :is-open="isMissingProductsDialogOpen"
                :ok-btn-text="$t('actions.close')"
                @onOk="isMissingProductsDialogOpen = false"
              >
                <template v-slot:header>
                  {{
                    $t('workPackages.missingProductsInAOExtracts', {
                      count: workpackage.missingProductsInAOExtracts.length,
                    })
                  }}
                </template>
                <template v-slot:body>
                  <v-expansion-panels accordion>
                    <v-expansion-panel
                      v-for="tsgKey in storeGroupsWithMissingProducts"
                      :key="tsgKey"
                    >
                      <v-expansion-panel-header>
                        {{ storegroupKeyNameMap[tsgKey] }} -
                        {{
                          $t('workPackages.missingProductsInStoreGroup', {
                            count: missingProductsInAOExtractsByTSGKey[tsgKey].length,
                          })
                        }}
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        {{ missingProductsInAOExtractsByTSGKey[tsgKey].join(', ') }}
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </template>
              </info-dialog>
            </v-col>
          </v-row>
        </template>
      </feature-toggle>
    </v-container>
    <v-container v-else fluid pr-0 pl-0 pb-0 class="empty-reveal">
      <v-row no-gutters class="header text-row" />
    </v-container>
    <incorrect-mappings-modal
      :show-dialog="showIncorrectMappingsModal"
      @closeDialog="closeIncorrectMappingsModal"
    />
    <alert-dialog
      :is-open="isRefreshConfirmOpen"
      :ok-btn-text="$t('actions.yesProceed')"
      :cancel-btn-text="$t('actions.cancel')"
      @onOk="confirmRefresh"
      @onCancel="isRefreshConfirmOpen = false"
    >
      <template v-slot:header>{{ $t('workPackages.refreshConfirm.header') }}</template>
      <template v-slot:body>{{ $t('workPackages.refreshConfirm.body') }}</template>
    </alert-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { get, isEmpty } from 'lodash';
import moment from 'moment';

import workpackageTypes from '@enums/workpackage-types';
import {
  enableAOExtracts,
  usePeriodIdsInAOExtracts,
  refreshWorkpackageFromDatabricks,
} from '@enums/feature-flags';
import { yearMonthDayHoursMinutesSecondsFormat } from '@enums/date-formats';
import numberFormats from '@enums/number-formats';
import featureFlagsMixin from '../../../mixins/featureFlags';

export default {
  mixins: [featureFlagsMixin],

  props: {
    workpackage: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      enableAOExtracts,
      usePeriodIdsInAOExtracts,
      showIncorrectMappingsModal: false,
      isMissingProductsDialogOpen: false,
      isRefreshConfirmOpen: false,
      numberFormats,
    };
  },
  computed: {
    ...mapState('workpackages', [
      'deletingIds',
      'exportingIds',
      'postingIds',
      'refreshingIds',
      'loading',
    ]),
    ...mapState('storeGroupRelationships', ['storegroupKeyNameMap']),
    ...mapGetters('workpackages', ['getAOExtractsByKey']),

    lastUpdatedText() {
      const lastUpdatedDate = this.getFormattedDateTime(this.workpackage.lastUpdated);
      if (
        this.isFeatureFlagEnabled(refreshWorkpackageFromDatabricks) &&
        this.workpackage.lastUpdatedBy
      ) {
        return this.$t('workPackages.lastUpdatedBy', {
          date: lastUpdatedDate,
          username: this.workpackage.lastUpdatedBy,
        });
      }
      return lastUpdatedDate;
    },

    deleting() {
      return this.deletingIds.includes(this.workpackage._id);
    },
    exporting() {
      return this.exportingIds.includes(this.workpackage._id);
    },
    posting() {
      return this.postingIds.includes(this.workpackage._id);
    },
    refreshing() {
      return this.refreshingIds.includes(this.workpackage._id);
    },
    disabled() {
      return this.deleting || this.exporting || this.posting || this.refreshing;
    },
    isMasterWorkpackage() {
      return get(this.workpackage, 'type') === workpackageTypes.master;
    },

    getWorkpackageAssociatedPeriodIds() {
      const periodsIds = new Set();
      this.workpackage.aoExtracts.forEach(extract => {
        this.getAOExtractsByKey[extract.auditCorrelationId].periodIds.map(periodId =>
          periodsIds.add(periodId)
        );
      });
      return Array.from(periodsIds);
    },

    missingProductsInAOExtractsByTSGKey() {
      if (isEmpty(this.workpackage.missingProductsInAOExtracts)) {
        return {};
      }
      return this.workpackage.missingProductsInAOExtracts.reduce((acc, product) => {
        const tsgKey = product.toolStoreGroupKey;
        if (!acc[tsgKey]) {
          acc[tsgKey] = [];
        }
        acc[tsgKey].push(product.productKeyDisplay);
        return acc;
      }, {});
    },

    storeGroupsWithMissingProducts() {
      return Object.keys(this.storegroupKeyNameMap).filter(
        key => this.missingProductsInAOExtractsByTSGKey[key]
      );
    },
  },

  methods: {
    ...mapActions('workpackages', ['refreshWorkpackage']),

    closeIncorrectMappingsModal() {
      this.showIncorrectMappingsModal = false;
    },

    confirmRefresh() {
      this.isRefreshConfirmOpen = false;
      this.refreshWorkpackage({ workpackageId: this.workpackage._id });
    },

    getFormattedDateTime(datetime) {
      const localtime = moment
        .utc(datetime)
        .local()
        .format(yearMonthDayHoursMinutesSecondsFormat);
      const [date, time] = localtime.split(' ');
      const [hours, minutes] = time.split(':');
      return `${this.formatLocalisedDate(date)} ${[hours, minutes].join(':')}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.refresh-btn {
  background-color: $pricing-primary !important;
  color: white !important;
  min-width: unset !important;
  padding: unset !important;
  height: 3rem !important;
  width: 3rem;
  .v-icon {
    color: white !important;
    transform: rotate(45deg);
  }
}

.text-row {
  font-weight: normal;
  margin-left: -1.7rem;
}

.empty-reveal {
  padding-top: 0.2rem;
}

.ao-extracts {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

::v-deep {
  .v-expansion-panel-header {
    font-size: 1.4rem;
  }
  .v-expansion-panel-content__wrap {
    padding: 0rem 2rem 1rem;
  }
}
</style>
